import React, { useState } from 'react';

import { Box, Button, Divider, Stack, TextField } from '@mui/material';

import { Capacity, DEFAULT_CAPACITY } from '~/consts/capacities';

import ReservationService from '~/services/ReservationService';

import { formatValidationMessages } from '~/utils/formatValidationMessages';
import { reportError } from '~/utils/reportError';

import ErrorListDisplay from '../ErrorListDisplay';

import { buttonMessages, buttonStates } from './states/submitButton';

interface Props {
  onCancel: () => void;
  onRoomTypeCapacityUpdated: (capacity: Capacity) => void;
  propertyId: string;
  roomTypeId: string;
  roomRateId: string;
  capacity?: Capacity;
}

export default function RoomTypeCapacityForm({
  onCancel,
  onRoomTypeCapacityUpdated,
  propertyId,
  roomTypeId,
  roomRateId,
  capacity: initialCapacity,
}: Props) {
  const [capacity, setCapacity] = useState<Capacity>(
    initialCapacity ? JSON.parse(JSON.stringify(initialCapacity)) : DEFAULT_CAPACITY,
  );
  const [saveState, setSaveState] = useState(buttonStates.default);
  const [errors, setErrors] = useState<Record<string, Array<string>> | null>(null);

  const handleValueChange = (fieldName: keyof Capacity, value: string) => {
    setCapacity((prev) => ({
      ...prev,
      [fieldName]: parseInt(value) || 0,
    }));
  };

  const handleSubmit = async () => {
    try {
      setErrors(null);
      setSaveState(buttonStates.saving);

      const response = capacity.id
        ? await ReservationService.updateRoomTypeCapacity(capacity, capacity.id, propertyId, roomTypeId, roomRateId)
        : await ReservationService.createRoomTypeCapacity(capacity, propertyId, roomTypeId, roomRateId);

      setSaveState(buttonStates.saved);
      onRoomTypeCapacityUpdated(response.result);
    } catch (error) {
      const validationErrors = error.name === 'ValidationError' ? error.errors : null;
      setSaveState(buttonStates.failed);
      setErrors(validationErrors);
      reportError(error);
    }
  };

  const formRow = (label: string, fieldName: keyof Capacity) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box sx={{ flex: 1 }}>{label}</Box>
      <Box sx={{ flex: 1 }}>
        <TextField
          type="number"
          inputProps={{ min: 0, step: 1 }}
          value={capacity[fieldName]}
          onChange={(e) => handleValueChange(fieldName, e.target.value)}
          fullWidth
          size="small"
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 1 }}>
      {formRow('Adults', 'adults')}
      {formRow('Children', 'children')}
      {formRow('Infants', 'infants')}
      {formRow('Teenagers', 'teenagers')}

      <Divider sx={{ my: 2 }} />
      {errors && (
        <Box sx={{ mb: 2 }}>
          <ErrorListDisplay messages={formatValidationMessages(errors)} />
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" disabled={saveState === buttonStates.saving} onClick={handleSubmit}>
          {buttonMessages[saveState]}
        </Button>
      </Stack>
    </Box>
  );
}
